import api from './api';

export interface IntakeFormType {
  id: number;
  name: string;
  short_code: string;
  description?: string;
  template_file_path?: string;
  template_filename?: string;
  is_active: boolean;
  sort_order: number;
  created_at: string;
  updated_at: string;
  service_types?: ServiceTypeMapping[];
}

export interface ServiceTypeMapping {
  id: number;
  name: string;
  pivot: {
    is_required: boolean;
    sort_order: number;
  };
}

export interface CreateIntakeFormTypeData {
  name: string;
  short_code: string;
  description?: string;
  template_file?: File;
  is_active?: boolean;
  sort_order?: number;
  service_types?: Array<{
    id: number;
    is_required: boolean;
    sort_order: number;
  }>;
}

export interface UpdateIntakeFormTypeData
  extends Partial<CreateIntakeFormTypeData> {
  id: number;
}

class IntakeFormTypeService {
  async getAll(): Promise<IntakeFormType[]> {
    const response = await api.get('/admin/intake-form-types');
    return response.data;
  }

  async getById(id: number): Promise<IntakeFormType> {
    const response = await api.get(`/admin/intake-form-types/${id}`);
    return response.data;
  }

  async create(data: CreateIntakeFormTypeData): Promise<IntakeFormType> {
    const formData = new FormData();

    // Add basic fields
    formData.append('name', data.name);
    formData.append('short_code', data.short_code);
    if (data.description) formData.append('description', data.description);
    if (data.is_active !== undefined)
      formData.append('is_active', data.is_active.toString());
    if (data.sort_order !== undefined)
      formData.append('sort_order', data.sort_order.toString());

    // Add template file if provided
    if (data.template_file) {
      formData.append('template_file', data.template_file);
      console.log('Create - File details:', {
        name: data.template_file.name,
        size: data.template_file.size,
        type: data.template_file.type,
      });
    }

    // Add service types if provided
    if (data.service_types) {
      data.service_types.forEach((serviceType, index) => {
        formData.append(
          `service_types[${index}][id]`,
          serviceType.id.toString()
        );
        formData.append(
          `service_types[${index}][is_required]`,
          serviceType.is_required.toString()
        );
        formData.append(
          `service_types[${index}][sort_order]`,
          serviceType.sort_order.toString()
        );
      });
    }

    const response = await api.post('/admin/intake-form-types', formData, {
      headers: {
        'Content-Type': 'multipart/form-data',
      },
    });
    return response.data;
  }

  async update(
    id: number,
    data: UpdateIntakeFormTypeData
  ): Promise<IntakeFormType> {
    // If there's a file, use FormData, otherwise use JSON
    if (data.template_file) {
      const formData = new FormData();

      // Add basic fields - always include them if they exist
      if (data.name !== undefined) formData.append('name', data.name);
      if (data.short_code !== undefined)
        formData.append('short_code', data.short_code);
      if (data.description !== undefined)
        formData.append('description', data.description || '');
      if (data.is_active !== undefined)
        formData.append('is_active', data.is_active ? '1' : '0');
      if (data.sort_order !== undefined)
        formData.append('sort_order', data.sort_order.toString());

      // Add template file
      formData.append('template_file', data.template_file);

      // Add service types if provided
      if (data.service_types) {
        data.service_types.forEach((serviceType, index) => {
          formData.append(
            `service_types[${index}][id]`,
            serviceType.id.toString()
          );
          formData.append(
            `service_types[${index}][is_required]`,
            serviceType.is_required.toString()
          );
          formData.append(
            `service_types[${index}][sort_order]`,
            serviceType.sort_order.toString()
          );
        });
      }
      // Laravel file upload fix: use POST with _method=PUT
      formData.append('_method', 'PUT');

      // Debug: Log the FormData contents
      console.log('Update FormData contents:');
      formData.forEach((value, key) => {
        console.log(`${key}: ${value}`);
      });

      // Additional file debugging
      if (data.template_file) {
        console.log('File details:', {
          name: data.template_file.name,
          size: data.template_file.size,
          type: data.template_file.type,
        });
      }

      const response = await api.post(
        `/admin/intake-form-types/${id}`,
        formData,
        {
          headers: {
            'Content-Type': 'multipart/form-data',
          },
        }
      );
      return response.data;
    } else {
      // Use JSON for updates without files
      const jsonData: any = {};

      if (data.name !== undefined) jsonData.name = data.name;
      if (data.short_code !== undefined) jsonData.short_code = data.short_code;
      if (data.description !== undefined)
        jsonData.description = data.description;
      if (data.is_active !== undefined) jsonData.is_active = data.is_active;
      if (data.sort_order !== undefined) jsonData.sort_order = data.sort_order;
      if (data.service_types) jsonData.service_types = data.service_types;

      console.log('Update JSON data:', jsonData);

      const response = await api.put(
        `/admin/intake-form-types/${id}`,
        jsonData,
        {
          headers: {
            'Content-Type': 'application/json',
          },
        }
      );
      return response.data;
    }
  }

  async delete(id: number): Promise<void> {
    await api.delete(`/admin/intake-form-types/${id}`);
  }

  async downloadTemplate(id: number): Promise<Blob> {
    const response = await api.get(
      `/admin/intake-form-types/${id}/download-template`,
      {
        responseType: 'blob',
      }
    );
    return response.data;
  }

  async getByServiceType(serviceTypeId: number): Promise<IntakeFormType[]> {
    const response = await api.get(
      `/admin/service-types/${serviceTypeId}/intake-form-types`
    );
    return response.data;
  }
}

export default new IntakeFormTypeService();
